import { useMutation, useQuery } from '@apollo/client'
import { useToast } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import {
  CREATE_DEPARTMENT_MUTATION,
  CREATE_FACILITY_MUTATION,
  CREATE_HOLIDAY_ALLOWANCE_MUTATION,
  CREATE_SALARY_CODE_MUTATION,
  DELETE_DEPARTMENT_MUTATION,
  DELETE_FACILITY_MUTATION,
  DELETE_HOLIDAY_ALLOWANCE_MUTATION,
  DELETE_SALARY_CODE_MUTATION,
  PATCH_DEPARTMENT_MUTATION,
  PATCH_FACILITY_MUTATION,
  PATCH_HOLIDAY_ALLOWANCE_MUTATION,
  PATCH_SALARY_CODE_MUTATION,
} from '@/modules/companies/mutations'
import {
  ALL_DEPARTMENTS_QUERY,
  ALL_FACILITIES_QUERY,
  ALL_HOLIDAY_ALLOWANCES_QUERY,
  ALL_SALARY_CODES_QUERY,
} from '@/modules/companies/queries'
import type {
  CreateDepartmentMutation,
  CreateDepartmentMutationVariables,
  CreateSalaryCodeMutation,
  CreateSalaryCodeMutationVariables,
  DeleteDepartmentMutation,
  DeleteDepartmentMutationVariables,
  DeleteSalaryCodeMutation,
  DeleteSalaryCodeMutationVariables,
  PatchDepartmentMutation,
  PatchDepartmentMutationVariables,
  PatchSalaryCodeMutation,
  PatchSalaryCodeMutationVariables,
} from '@/modules/companies/types.graphql'
import { useOnErrorAuto } from '@/utils/hooks'
import { getNodes } from '@/utils/graphql'

export function useDepartmentMutations() {
  const translations = useTranslate({
    create: 'companies.toasts.department-created',
    update: 'companies.toasts.department-updated',
    delete: 'companies.toasts.department-deleted',
  })

  const addToast = useToast()
  const onError = useOnErrorAuto()
  const [createDepartment] = useMutation<
    CreateDepartmentMutation,
    CreateDepartmentMutationVariables
  >(CREATE_DEPARTMENT_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.create)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: ALL_DEPARTMENTS_QUERY }],
  })

  const [patchDepartment] = useMutation<
    PatchDepartmentMutation,
    PatchDepartmentMutationVariables
  >(PATCH_DEPARTMENT_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: ALL_DEPARTMENTS_QUERY }],
  })

  const [deleteDepartment] = useMutation<
    DeleteDepartmentMutation,
    DeleteDepartmentMutationVariables
  >(DELETE_DEPARTMENT_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.delete)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: ALL_DEPARTMENTS_QUERY }],
  })

  return { createDepartment, patchDepartment, deleteDepartment }
}

export function useFacilityMutations() {
  const translations = useTranslate({
    create: 'companies.toasts.facility-created',
    update: 'companies.toasts.facility-updated',
    delete: 'companies.toasts.facility-deleted',
  })

  const addToast = useToast()
  const onError = useOnErrorAuto()
  const [createFacility] = useMutation(CREATE_FACILITY_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.create)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: ALL_FACILITIES_QUERY }],
  })

  const [patchFacility] = useMutation(PATCH_FACILITY_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: ALL_FACILITIES_QUERY }],
  })

  const [deleteFacility] = useMutation(DELETE_FACILITY_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.delete)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [{ query: ALL_FACILITIES_QUERY }],
  })

  return { createFacility, patchFacility, deleteFacility }
}

export function useSalaryCodeMutations() {
  const translations = useTranslate({
    create: 'companies.toasts.salary-code-created',
    update: 'companies.toasts.salary-code-updated',
    delete: 'companies.toasts.salary-code-deleted',
  })

  const addToast = useToast()
  const onError = useOnErrorAuto()

  const [createSalaryCode] = useMutation<
    CreateSalaryCodeMutation,
    CreateSalaryCodeMutationVariables
  >(CREATE_SALARY_CODE_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.create)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false, isDiet: false },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false, isDiet: true },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
      },
    ],
  })

  const [patchSalaryCode] = useMutation<
    PatchSalaryCodeMutation,
    PatchSalaryCodeMutationVariables
  >(PATCH_SALARY_CODE_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.update)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false, isDiet: false },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false, isDiet: true },
      },
    ],
  })

  const [deleteSalaryCode] = useMutation<
    DeleteSalaryCodeMutation,
    DeleteSalaryCodeMutationVariables
  >(DELETE_SALARY_CODE_MUTATION, {
    onCompleted: () => {
      addToast('success', translations.delete)
    },
    onError: error => onError()(error),
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false, isDiet: false },
      },
      {
        query: ALL_SALARY_CODES_QUERY,
        variables: { showInManualTimeEntryOvertime: false, isDiet: true },
      },
    ],
  })

  return { createSalaryCode, patchSalaryCode, deleteSalaryCode }
}

export function useHolidayAllowanceMutations() {
  const translations = useTranslate({
    create: 'companies.toasts.holiday-allowance-created',
    update: 'companies.toasts.holiday-allowance-updated',
    delete: 'companies.toasts.holiday-allowance-deleted',
  })

  const addToast = useToast()
  const onError = useOnErrorAuto()

  const [createHolidayAllowance] = useMutation(
    CREATE_HOLIDAY_ALLOWANCE_MUTATION,
    {
      onCompleted: () => {
        addToast('success', translations.create)
      },
      onError: error => onError()(error),
      awaitRefetchQueries: true,
      refetchQueries: [{ query: ALL_HOLIDAY_ALLOWANCES_QUERY }],
    }
  )

  const [patchHolidayAllowance] = useMutation(
    PATCH_HOLIDAY_ALLOWANCE_MUTATION,
    {
      onCompleted: () => {
        addToast('success', translations.update)
      },
      onError: error => onError()(error),
      awaitRefetchQueries: true,
      refetchQueries: [{ query: ALL_HOLIDAY_ALLOWANCES_QUERY }],
    }
  )

  const [deleteHolidayAllowance] = useMutation(
    DELETE_HOLIDAY_ALLOWANCE_MUTATION,
    {
      onCompleted: () => {
        addToast('success', translations.delete)
      },
      onError: error => onError()(error),
      awaitRefetchQueries: true,
      refetchQueries: [{ query: ALL_HOLIDAY_ALLOWANCES_QUERY }],
    }
  )

  return {
    createHolidayAllowance,
    patchHolidayAllowance,
    deleteHolidayAllowance,
  }
}

export function useHolidayAllowances() {
  const { data } = useQuery(ALL_HOLIDAY_ALLOWANCES_QUERY)
  return getNodes(data?.allHolidayAllowances)
}
